/**
 * Event WebSocket API Type Definitions
 * Defines all types for WebPush WebSocket communication
 */

/**
 * WebPush message interface from server
 */
export interface WebPushMessage {
  type: 'webpush';
  code: WebPushEventCode;
  message: string;
  image?: string;
  file?: string;
}

/**
 * WebPush event codes - Message only events
 */
export type MessageOnlyEventCode = 
  | 'ALARM_FOTA_SUCCESS'
  | 'ALARM_FOTA_FAIL'
  | 'ALARM_GEOFENCE_ENTER'
  | 'ALARM_GEOFENCE_EXIT'
  | 'ALARM_GEOFENCE_PASS'
  | 'DEVICE_CONNECT'
  | 'DEVICE_DISCONNECT'
  | 'ALARM_PARK_MOTION'
  | 'ALARM_PARK_IN'
  | 'ALARM_PARK_OUT'
  | 'ALARM_TEMPERATURE_OVER'
  | 'ALARM_TEMPERATURE_LTE_OVER'
  | 'ALARM_LOW_VOLTAGE'
  | 'ALARM_SETTING_SAVED'
  | 'SERVER_RESTART';

/**
 * WebPush event codes - Image included events
 */
export type ImageIncludedEventCode =
  | 'ALARM_SPEED'
  | 'ALARM_EVENT'
  | 'ALARM_PARK_EVENT'
  | 'ALARM_ACCELERATION'
  | 'ALARM_HARSHBRAKING'
  | 'ALARM_SHARPTURN'
  | 'ALARM_MANUAL'
  | 'ALARM_DROWSY'
  | 'ALARM_DROWSY_SECOND'
  | 'ALARM_MASK_OFF'
  | 'ALARM_CALLING'
  | 'ALARM_SMOKING'
  | 'ALARM_DISTRACTED'
  | 'ALARM_UNDETECTED'
  | 'ALARM_DETECTED';

/**
 * All WebPush event codes
 */
export type WebPushEventCode = MessageOnlyEventCode | ImageIncludedEventCode;

/**
 * WebPush connection states
 */
export type EventConnectionState = 
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'failed'
  | 'reconnecting';

/**
 * Event WebSocket event types
 */
export type EventWsEvent =
  | 'open'
  | 'close'
  | 'error'
  | 'message'
  | 'webpush'
  | 'raw';

/**
 * Event handler type
 */
export type EventWsEventHandler<T = any> = (data: T) => void;

/**
 * Event WebSocket SDK configuration
 */
export interface EventWebSocketSDKOptions {
  baseUrl?: string;
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
  heartbeatInterval?: number;
  psnFilters?: string[];
  enablePsnFiltering?: boolean;
}

/**
 * PSN filter configuration
 */
export interface PsnFilterConfig {
  enableFiltering: boolean;
  allowedPsns: Set<string>;
  extractPsnFromMessage: (message: string) => string | null;
}

/**
 * Default configuration values
 */
export const DEFAULT_EVENT_WEBSOCKET_CONFIG: Required<EventWebSocketSDKOptions> = {
  baseUrl: 'wss://webpush-websocket-dev.fleeta.io',
  maxReconnectAttempts: 5,
  reconnectDelay: 1000,
  heartbeatInterval: 300000,  // 5분 (AWS 권장사항)
  psnFilters: [],
  enablePsnFiltering: false
};

/**
 * WebPush event code descriptions
 */
export const WEBPUSH_EVENT_DESCRIPTIONS: Record<WebPushEventCode, string> = {
  // Message only events
  'ALARM_FOTA_SUCCESS': 'FOTA (Firmware Update) Success',
  'ALARM_FOTA_FAIL': 'FOTA (Firmware Update) Failed',
  'ALARM_GEOFENCE_ENTER': 'Geofence Entry',
  'ALARM_GEOFENCE_EXIT': 'Geofence Exit',
  'ALARM_GEOFENCE_PASS': 'Geofence Pass Through',
  'DEVICE_CONNECT': 'Device Connected',
  'DEVICE_DISCONNECT': 'Device Disconnected',
  'ALARM_PARK_MOTION': 'Parking Motion Detected',
  'ALARM_PARK_IN': 'Parking Mode Entered',
  'ALARM_PARK_OUT': 'Parking Mode Exited',
  'ALARM_TEMPERATURE_OVER': 'High Temperature Alert',
  'ALARM_TEMPERATURE_LTE_OVER': 'LTE High Temperature Shutdown',
  'ALARM_LOW_VOLTAGE': 'Low Voltage Shutdown',
  'ALARM_SETTING_SAVED': 'File Recording Completed',
  'SERVER_RESTART': 'Server Restart Notification',
  
  // Image included events
  'ALARM_SPEED': 'Speed Limit Exceeded',
  'ALARM_EVENT': 'Impact Event',
  'ALARM_PARK_EVENT': 'Parking Impact Event',
  'ALARM_ACCELERATION': 'Harsh Acceleration',
  'ALARM_HARSHBRAKING': 'Harsh Braking',
  'ALARM_SHARPTURN': 'Sharp Turn',
  'ALARM_MANUAL': 'Emergency Recording',
  'ALARM_DROWSY': 'Drowsiness Detected',
  'ALARM_DROWSY_SECOND': 'Secondary Drowsiness Detected',
  'ALARM_MASK_OFF': 'Mask Not Worn',
  'ALARM_CALLING': 'Phone Call Gesture Detected',
  'ALARM_SMOKING': 'Smoking Gesture Detected',
  'ALARM_DISTRACTED': 'Driver Distraction',
  'ALARM_UNDETECTED': 'Driver Not Detected',
  'ALARM_DETECTED': 'Driver Detected'
};

/**
 * Check if event code includes image
 */
export function isImageIncludedEvent(code: WebPushEventCode): code is ImageIncludedEventCode {
  const imageIncludedCodes: ImageIncludedEventCode[] = [
    'ALARM_SPEED', 'ALARM_EVENT', 'ALARM_PARK_EVENT', 'ALARM_ACCELERATION',
    'ALARM_HARSHBRAKING', 'ALARM_SHARPTURN', 'ALARM_MANUAL', 'ALARM_DROWSY',
    'ALARM_DROWSY_SECOND', 'ALARM_MASK_OFF', 'ALARM_CALLING', 'ALARM_SMOKING',
    'ALARM_DISTRACTED', 'ALARM_UNDETECTED', 'ALARM_DETECTED'
  ];
  
  return imageIncludedCodes.includes(code as ImageIncludedEventCode);
}

/**
 * Categorize WebPush events by type
 */
export const WEBPUSH_EVENT_CATEGORIES = {
  device: ['DEVICE_CONNECT', 'DEVICE_DISCONNECT'] as const,
  fota: ['ALARM_FOTA_SUCCESS', 'ALARM_FOTA_FAIL'] as const,
  geofence: ['ALARM_GEOFENCE_ENTER', 'ALARM_GEOFENCE_EXIT', 'ALARM_GEOFENCE_PASS'] as const,
  parking: ['ALARM_PARK_MOTION', 'ALARM_PARK_IN', 'ALARM_PARK_OUT', 'ALARM_PARK_EVENT'] as const,
  temperature: ['ALARM_TEMPERATURE_OVER', 'ALARM_TEMPERATURE_LTE_OVER'] as const,
  voltage: ['ALARM_LOW_VOLTAGE'] as const,
  driving: ['ALARM_SPEED', 'ALARM_EVENT', 'ALARM_ACCELERATION', 'ALARM_HARSHBRAKING', 'ALARM_SHARPTURN'] as const,
  driver: ['ALARM_DROWSY', 'ALARM_DROWSY_SECOND', 'ALARM_MASK_OFF', 'ALARM_CALLING', 'ALARM_SMOKING', 'ALARM_DISTRACTED', 'ALARM_UNDETECTED', 'ALARM_DETECTED'] as const,
  manual: ['ALARM_MANUAL'] as const,
  system: ['ALARM_SETTING_SAVED', 'SERVER_RESTART'] as const
} as const;